import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Category } from "../../model/asset.model";
import { AssetCard } from "../AssetCard/AssetCard";
import styles from "./CategoryRow.module.css";

export type CategoryRowProps = {
  category: Category;
  onViewAll?: (categoryId: string) => void;
  /** Poster tag uses same behavior as typing this text into browse search */
  onPrimaryTagSearch?: (label: string) => void;
  /** Hide “View all” (e.g. browse Enter category focus — already viewing that category) */
  hideViewAll?: boolean;
  /** Merged onto the root `<section>` (e.g. single-title “You might also like” band) */
  className?: string;
};

const ChevronLeft: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M15 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function CategoryRow({
  category,
  onViewAll,
  onPrimaryTagSearch,
  hideViewAll = false,
  className,
}: CategoryRowProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollerInnerRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflow = scrollWidth > clientWidth + 2;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const epsilon = 6;

    setShowArrows(overflow);
    setCanScrollLeft(overflow && scrollLeft > epsilon);
    setCanScrollRight(overflow && scrollLeft < maxScroll - epsilon);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    const inner = scrollerInnerRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    if (inner) ro.observe(inner);

    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(updateScrollState);
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [category.assets, updateScrollState]);

  const scrollByDirection = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(280, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <section
      className={[styles.row, className].filter(Boolean).join(" ")}
    >
      <header
        className={
          hideViewAll
            ? `${styles.header} ${styles.headerNoViewAll}`
            : styles.header
        }
      >
        <div className={styles.name}>{category.name}</div>
        {!hideViewAll ? (
          <button
            type="button"
            className={styles.viewAll}
            onClick={() => onViewAll?.(category.id)}
          >
            View all
          </button>
        ) : null}
      </header>

      <div className={styles.trackOuter}>
        <div
          ref={scrollerRef}
          className={styles.scroller}
          aria-label={`Assets in ${category.name}`}
        >
          <div ref={scrollerInnerRef} className={styles.scrollerInner}>
            {category.assets.map((asset) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                onPrimaryTagSearch={onPrimaryTagSearch}
              />
            ))}
          </div>
        </div>

        {showArrows ? (
          <>
            <button
              type="button"
              className={`${styles.navBtn} ${styles.navBtnPrev}`}
              onClick={() => scrollByDirection(-1)}
              disabled={!canScrollLeft}
              aria-label={`Scroll ${category.name} left`}
              title="Previous titles"
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className={`${styles.navBtn} ${styles.navBtnNext}`}
              onClick={() => scrollByDirection(1)}
              disabled={!canScrollRight}
              aria-label={`Scroll ${category.name} right`}
              title="More titles"
            >
              <ChevronRight />
            </button>
          </>
        ) : null}
      </div>
    </section>
  );
}
